export const CANVAS_PATH = "/app";

const STATUS_ID = "canvas-navigation-status";

let canvasPreload: Promise<void> | null = null;
let navigationQueued = false;

/**
 * Fetch and evaluate the editor route while the visitor is still reading a
 * public page. The real navigation remains a full page load so the live demo's
 * Zustand state can never become the visitor's first saved project.
 *
 * A speculative failure is deliberately swallowed. Following the link still
 * fetches a fresh index document, whose normal chunk-reload recovery owns any
 * real deploy race.
 */
export function preloadCanvas(): Promise<void> {
  if (import.meta.env.SSR || typeof window === "undefined") {
    return Promise.resolve();
  }
  if (canvasPreload) return canvasPreload;

  canvasPreload = import("@/components/app-shell")
    .then(() => undefined)
    .catch(() => {
      canvasPreload = null;
    });
  return canvasPreload;
}

/** Warm a visible canvas CTA without competing with the public page's paint. */
export function scheduleCanvasPreload(): () => void {
  if (
    import.meta.env.SSR ||
    typeof window === "undefined" ||
    saveDataEnabled()
  ) {
    return () => undefined;
  }

  let cancelled = false;
  const warm = () => {
    if (!cancelled) void preloadCanvas();
  };

  if ("requestIdleCallback" in window) {
    const handle = window.requestIdleCallback(warm, { timeout: 2_500 });
    return () => {
      cancelled = true;
      window.cancelIdleCallback(handle);
    };
  }

  const handle = globalThis.setTimeout(warm, 1_200);
  return () => {
    cancelled = true;
    globalThis.clearTimeout(handle);
  };
}

/**
 * Show feedback for one paint, then perform the clean full navigation. The
 * single animation-frame delay is intentional: without it, a slow response
 * can leave the clicked public page looking inert until the new document wins.
 */
export function navigateToCanvas(
  href: string = CANVAS_PATH,
): void {
  if (typeof window === "undefined" || navigationQueued) return;
  navigationQueued = true;

  showNavigationStatus();
  void preloadCanvas();

  const navigate = () => window.location.assign(href);
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => window.setTimeout(navigate, 0));
  } else {
    window.setTimeout(navigate, 0);
  }
}

function saveDataEnabled(): boolean {
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  return connection?.saveData === true;
}

function showNavigationStatus(): void {
  const body = document.body;
  if (!body) return;

  body.setAttribute("aria-busy", "true");
  if (document.getElementById(STATUS_ID)) return;

  const status = document.createElement("div");
  status.id = STATUS_ID;
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");

  const label = document.createElement("span");
  label.textContent = "Opening canvas…";
  status.append(label);
  body.append(status);

  // A back/forward-cache restore revives this document exactly as it was.
  window.addEventListener("pageshow", resetNavigationStatus, { once: true });
}

function resetNavigationStatus(): void {
  document.body?.removeAttribute("aria-busy");
  document.getElementById(STATUS_ID)?.remove();
  navigationQueued = false;
}
